import type { Router } from "vue-router";
import { gettext } from "@/services/translation";
import { showReloadToast } from "@/services/toasts";
import { trackEvent, Event } from "@/services/analytics/sentry";
import { canRetryDynamicImport, getDynamicImportErrorKey, isDynamicImportError, recordDynamicImportAttempt } from "@/utilities/dynamicImportRetry";

const MaxAttempts = 2;
const RetryDelayMs = 500;

/**
 * Recovers a route-level lazy view failing to load its chunk (transient network/CDN blip) by
 * re-navigating internally instead of a full page reload, so in-progress user input elsewhere in the
 * app is never lost. Falls back to a dismissible reload prompt once the retry budget is exhausted. See
 * issue #1066.
 */
export const registerDynamicImportRecovery = (router: Router): void => {
  router.onError((error, to) => {
    if (!isDynamicImportError(error)) {
      return;
    }

    if (canRetryDynamicImport(error, MaxAttempts)) {
      recordDynamicImportAttempt(error);
      setTimeout(() => {
        router
          .replace(to.fullPath)
          .then(() => trackEvent(Event.DynamicImportRecovered, "info"))
          .catch(() => {
            // A further failure re-enters this same handler.
          });
      }, RetryDelayMs);
      return;
    }

    trackEvent(Event.DynamicImportFailed, "warning", { error: getDynamicImportErrorKey(error), route: to.fullPath });
    showReloadToast(gettext.$gettext("Nepodařilo se načíst část stránky. Zkus to prosím znovu."), gettext.$gettext("Obnovit stránku"));
  });
};
